import { type JSX } from 'react';
import type {
  Verifikationdetails_node_TEDEKBokfver__Associations_Konteringar as IVerifikationKonteringData,
  Verifikationdetails_node_TEDEKBokfver__Associations_Handelser as IHandelseData,
  Verifikationdetails_node_TEDEKBokfver as IVerifikationNodeData,
  Verifikationdetails_node_TEDEKBokfver__Associations_Handelser,
  Verifikationdetails_node_TEDEKBokfver__Associations_Handelser__Associations_Levfaktura__Associations_Fakturadel as IFakturadel,
  Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All,
} from './_QueryTypes/Verifikationdetails';
import {
  TEDEKBokfverVERKAT_KODEnum as VerifikationCategory,
  TEDEKBokfjournalSTATUSEnum as VerifikationStatus,
  TEDLRFakturaAttestTYPEnum,
  TEDInputSelectionCondition,
} from '../../../../../../../../../globalQueryTypes';
import query from './VerifikationQuery.gql';
import { Query } from '@apollo/client/react/components';
import { ApolloError } from '@apollo/client';
import { uniq } from 'ramda';
import mergeSort from '../../../../../../../../../Utils/mergeSort';
import { EkonomiskAnalysUnderlagQuery_node_TEDEKBokfver as IUnderlagQueryNode } from '../EkonomiskAnalysUnderlagQuery/_QueryTypes/EkonomiskAnalysUnderlagQuery';
import { Verifikationdetails_ekanalysverifikationreferenser } from './_QueryTypes/Verifikationdetails';

export { IVerifikationKonteringData, VerifikationStatus, VerifikationCategory };

interface IData {
  node: IVerifikationNodeData | null;
  ekanalysverifikationreferenser: Verifikationdetails_ekanalysverifikationreferenser[];
}

interface IVariables {
  id: string;
  customSelection?: (TEDInputSelectionCondition[] | null)[] | null;
}

interface IChildProps {
  verifikation: IVerifikation | undefined;
  loading: boolean;
  error: ApolloError | Error | undefined;
}

interface IProps {
  id: string;
  children: (childProps: IChildProps) => JSX.Element;
  skip?: boolean;
  customSelection: TEDInputSelectionCondition[][];
}

export interface IVerifikation {
  verNr: number;
  kategori: VerifikationCategory | null;
  info: IUniversalVerInfo | null;
  extendedInfo?: IExtendedVerInfo;
  konteringar: IVerifikationKonteringData[];
  standardVerNr: string;
  verifikationsText: string;
  underlag?: IUnderlag[];
  referenser?: IReferens[];
  innehallerAutomatkonteringar: boolean;
  backad?: boolean;
  harAndringsVerifikation?: boolean;
  innehallerAttestMeddelanden?: boolean;
  innehallerPeriodiserade?: boolean;
  innehallerKonteringskoder?: boolean;
  messages?:
    | Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All[]
    | null
    | undefined;
  sumDebet: string;
  sumKredit: string;
}

export interface IUnderlag {
  extension: string;
  id: string;
  source: DokumentUnderlagSource;
  blobId?: string;
  latestChange?: string;
  latestChangeBy?: string;
  description?: string;
}

export interface IReferens {
  id: string;
  verNr: string;
  bokfar: string;
  bokfDatum: string;
  text: string;
  notering: string;
  verkat: string;
  signature: string;
  niva: number;
}

type DokumentUnderlagSource = 'bokfOrder' | 'doc' | 'levFakturaAttest';

export type FakturabildFormat = 'SVEFAKTURA' | 'KOB';

interface IBenamningData<T> {
  benamning: string;
  value: T;
}

// Data that should be displayed for all verifications
interface IUniversalVerInfo {
  bokforingsDatum: IBenamningData<string>;
  bokfordAv: IBenamningData<string>;
  bokforingsGrupp: IBenamningData<string>;
  status: IBenamningData<VerifikationStatus>;
  enhet: IBenamningData<string>;
  journalNummer: IBenamningData<string>;
}

interface IExtendedVerInfo {
  leverantor: IBenamningData<string>;
  fakturaNr: IBenamningData<string>;
  forfalloDatum: IBenamningData<string>;
  attesteradAv: IBenamningData<string[]>;
  fakturaBilder: IBenamningData<string[]>;
  xmlFaktura?: IBenamningData<boolean | null>;
  xmlFormat?: IBenamningData<FakturabildFormat | undefined>;
  skannadSvefaktura: IBenamningData<boolean>;
  levfakturaId: IBenamningData<string>;
}

// Create an info-block
const info = <T extends {}>(
  benamning: string,
  value: T
): IBenamningData<T> => ({
  benamning,
  value,
});

const getAttestanter = (fakturadel: IFakturadel[]): string[] => {
  const attestanter = fakturadel.reduce<string[]>(
    (attestanterTotal, currentFakturadel) => {
      if (
        currentFakturadel._Associations &&
        currentFakturadel._Associations.Fakturaattest
      ) {
        const attestanterPerDel =
          currentFakturadel._Associations.Fakturaattest.reduce<string[]>(
            (attestanterPerFakturaattest, currentFakturaattest) => {
              if (
                (currentFakturaattest.TYP ===
                  TEDLRFakturaAttestTYPEnum.BESLUTSATTEST ||
                  currentFakturaattest.TYP ===
                    TEDLRFakturaAttestTYPEnum.MOTTAGNINGSATTEST) &&
                currentFakturaattest.Attestant
              ) {
                const reserv = currentFakturaattest.Reserv
                  ? ` - Reserv: ${currentFakturaattest.Reserv}`
                  : '';
                attestanterPerFakturaattest.push(
                  `${currentFakturaattest.Attestant}${reserv}`
                );
              }

              return attestanterPerFakturaattest;
            },
            []
          );
        attestanterTotal.push(...attestanterPerDel);
      }
      return attestanterTotal;
    },
    []
  );

  return uniq(attestanter);
};

const getExtendedInfo = (
  node: IVerifikationNodeData
): IExtendedVerInfo | undefined => {
  if (!node._Associations) {
    return undefined;
  }

  // TODO: refakturera leverantörsfaktura till en egen funktion, så kan vi ha en funktion per kategorisort, som bestämmer vilken extendedInfo som ska finnas.

  let handelse = getLeverantorHandelse(node._Associations.Handelser);

  // Om vi inte hittat någon leverantörhändelse på verifikationen, se om det finns någon ursprungsverifikation och försök att ta händelser därifrån istället
  if (
    !handelse &&
    node._Associations &&
    node._Associations.Ursprungsverifikation &&
    node._Associations.Ursprungsverifikation._Associations
  ) {
    handelse = getLeverantorHandelse(
      node._Associations.Ursprungsverifikation._Associations.Handelser
    );
  }

  if (!handelse) {
    return undefined;
  }

  if (!handelse || !handelse._Associations) {
    return undefined;
  }

  let leverantor = '';
  let fakturaNr = '';
  let forfalloDatum = '';
  let fakturaBilder: string[] = [];
  let xmlFaktura = false;
  let xmlFormat: FakturabildFormat | undefined;
  let skannadSvefaktura = false;
  let attesteradAv: string[] = [];
  let fakturaId: string = '';

  if (handelse._Associations.Leverantor) {
    leverantor = handelse._Associations.Leverantor.LeverantorNrNamn || '';
  }

  if (
    handelse._Associations.Levfaktura &&
    handelse._Associations.Levfaktura._Associations &&
    handelse._Associations.Levfaktura._Associations.Fakturadel
  ) {
    const fakturadel =
      handelse._Associations.Levfaktura._Associations.Fakturadel;
    attesteradAv = getAttestanter(fakturadel);
  }

  if (handelse._Associations.Levfaktura) {
    fakturaNr = handelse._Associations.Levfaktura.FAKTURA_NR || '';
    forfalloDatum = handelse._Associations.Levfaktura.FORFALLO_DATUM || '';
    fakturaId = handelse._Associations.Levfaktura.id;

    fakturaBilder = getFakturaInfo(handelse, 'BILD') || [];
    const {
      isXml,
      format,
      skannadSvefaktura: skannad,
    } = isXmlFaktura(handelse);
    xmlFaktura = isXml;
    xmlFormat = format;
    skannadSvefaktura = skannad || false;
  }

  return {
    leverantor: { benamning: 'Leverantör', value: leverantor },
    fakturaNr: { benamning: 'Fakturanr', value: fakturaNr },
    forfalloDatum: { benamning: 'Förfallodatum', value: forfalloDatum },
    fakturaBilder: { benamning: '', value: fakturaBilder },
    xmlFaktura: { benamning: '', value: xmlFaktura },
    xmlFormat: { benamning: '', value: xmlFormat },
    attesteradAv: { benamning: 'Attesterad av', value: attesteradAv },
    skannadSvefaktura: { benamning: '', value: skannadSvefaktura },
    levfakturaId: info('', fakturaId),
  };
};

export const getUnderlag = (
  node: IVerifikationNodeData | IUnderlagQueryNode
): IUnderlag[] => {
  const bokfOrderUnderlag: IUnderlag[] =
    node?._Associations?.Bokforingsorder?._Associations?.Dokument?.map(dok => ({
      id: dok?.id ?? '',
      blobId: dok.BINARDATA ?? '',
      description: dok.BESKRIVNING ?? '',
      extension: dok?.EXTENSION ?? '',
      source: 'bokfOrder',
    })) ?? [];

  const documents: IUnderlag[] =
    node._Associations?.Dokument_All?.map(dok => ({
      id: dok?.id ?? '',
      blobId: dok.BINARDATA ?? '',
      description: dok.BESKRIVNING ?? '',
      extension: dok?.EXTENSION ?? '',
      source: 'doc',
    })) ?? [];

  const levFakturaUnderlag: IUnderlag[] = [];

  node?._Associations?.Handelser?.forEach(handelse => {
    handelse._Associations?.Levfaktura?._Associations?.Dokument_All?.forEach(
      dok => {
        levFakturaUnderlag.unshift({
          extension: dok.EXTENSION ?? '',
          id: dok.id,
          blobId: dok.BINARDATA ?? '',
          description: dok.BESKRIVNING ?? '',
          latestChange: dok.SENAST_ANDRAD,
          latestChangeBy: dok.SENAST_ANDRAD_SIGNATUR ?? '',
          source: 'levFakturaAttest',
        });
      }
    );
  });
  levFakturaUnderlag.reverse();

  return bokfOrderUnderlag.concat(levFakturaUnderlag).concat(documents);
};

const getReferenser = (
  rawReferenser: Verifikationdetails_ekanalysverifikationreferenser[]
): IReferens[] => {
  if (!rawReferenser || rawReferenser.length === 0) {
    return [];
  }

  return rawReferenser.map(ref => ({
    id: ref.id,
    verNr: ref.vernr.toString(),
    bokfar: ref.bokfar,
    bokfDatum: ref.bokfdatum,
    text: ref.text ?? '',
    notering: ref.notering ?? '',
    verkat: ref.verkat ?? '',
    signature: ref.signature ?? '',
    niva: ref.niva,
  }));
};

const getLeverantorHandelse = (
  handelser:
    | Verifikationdetails_node_TEDEKBokfver__Associations_Handelser[]
    | null
): IHandelseData | undefined => {
  if (!handelser) {
    return undefined;
  }

  const levfakturaHandelser = handelser.filter(
    handelse => handelse._Associations && handelse._Associations.Levfaktura
  );

  if (levfakturaHandelser.length === 0) {
    return undefined;
  }

  return levfakturaHandelser[0];
};

const isXmlFaktura = (
  handelse: IHandelseData
): {
  isXml: boolean;
  format?: FakturabildFormat;
  skannadSvefaktura?: boolean;
} => {
  const fakturaBilder =
    handelse &&
    handelse._Associations &&
    handelse._Associations.Levfaktura &&
    handelse._Associations.Levfaktura._Associations &&
    handelse._Associations.Levfaktura._Associations.Fakturasidor;

  const levFaktura =
    handelse && handelse._Associations && handelse._Associations.Levfaktura;

  if (!fakturaBilder) {
    return { isXml: false };
  }

  const isXml = fakturaBilder.some(fakturaBild => {
    if (
      fakturaBild._Associations &&
      fakturaBild._Associations.Blob &&
      fakturaBild._Associations.Blob.XML
    ) {
      return true;
    } else {
      return false;
    }
  });

  let format: FakturabildFormat | undefined;

  if (isXml) {
    if (fakturaBilder[0].FORMAT === 'KOB') {
      format = 'KOB';
    } else if (fakturaBilder[0].FORMAT === 'SVEFAKTURA') {
      format = 'SVEFAKTURA';
    }
  }

  return {
    isXml,
    format,
    skannadSvefaktura: isXml && (levFaktura?.SkannadSvefaktura || false),
  };
};

const getFakturaInfo = (
  handelse: IHandelseData,
  element: 'BILD' | 'XML'
): string[] | null => {
  const fakturaBilder =
    handelse &&
    handelse._Associations &&
    handelse._Associations.Levfaktura &&
    handelse._Associations.Levfaktura._Associations &&
    handelse._Associations.Levfaktura._Associations.Fakturasidor;

  if (!fakturaBilder) {
    return null;
  }

  const sortedFakturaBilder = mergeSort(fakturaBilder, (a, b) => {
    if (a.SIDNR == null || b.SIDNR == null) {
      return 0;
    }
    return a.SIDNR - b.SIDNR;
  });

  return sortedFakturaBilder
    .map(fakturaBild => {
      if (
        fakturaBild._Associations &&
        fakturaBild &&
        fakturaBild._Associations &&
        fakturaBild._Associations.Blob &&
        fakturaBild._Associations.Blob[element]
      ) {
        return fakturaBild._Associations.Blob[element];
      } else {
        return null;
      }
    })
    .filter(fakturaBild => fakturaBild !== null) as string[];
};

const VerifikationQuery = (props: IProps) => {
  return (
    <Query<IData, IVariables>
      query={query}
      variables={{ id: props.id, customSelection: props.customSelection }}
      skip={props.skip}
    >
      {({ data, loading, error }) => {
        if (loading) {
          return props.children({
            verifikation: undefined,
            error: undefined,
            loading: true,
          });
        } else if (error) {
          return props.children({
            verifikation: undefined,
            error: error,
            loading: false,
          });
        } else if (data == null || data.node == null) {
          return props.children({
            verifikation: undefined,
            error: new Error('Got no data'),
            loading: false,
          });
        }

        const node = data.node;

        const verifikation: IVerifikation = {
          verNr: node.VER_NR,
          kategori: node.VERKAT_KOD,
          verifikationsText: node.TEXT || '',
          backad: node.BACKAD,
          harAndringsVerifikation: Boolean(node.HarAndringsVerifikation),
          info: node._Associations
            ? {
                bokforingsDatum: info('Bokföringsdatum', node.DATUM),
                bokfordAv: info(
                  'Bokförd av',
                  (node._Associations.Anvandare &&
                    node._Associations.Anvandare.Name) ||
                    ''
                ),
                bokforingsGrupp: info(
                  'Bokföringsgrupp',
                  node._Associations.Bokfgrupp.BENAMNING
                ),
                status: info(
                  'Status',
                  node._Associations.Bokforingsjournal.STATUS
                ),
                journalNummer: info(
                  'Journalnummer',
                  node._Associations.Bokforingsjournal.JOURNAL_NR.toString()
                ),
                enhet: info('Enhet', 'kr'),
              }
            : null,
          extendedInfo: getExtendedInfo(node),
          konteringar: node._Associations
            ? node._Associations.Konteringar || []
            : [],
          innehallerAutomatkonteringar:
            node._Associations?.Konteringar?.some(
              kont => (kont.AUTOKONT_NIVA ?? -1) >= 0
            ) ?? false,
          innehallerPeriodiserade:
            node._Associations?.Konteringar?.some(
              kont =>
                kont.PeriodiseradFranDatum != null ||
                kont.PeriodiseradTillDatum != null
            ) ?? false,
          innehallerKonteringskoder:
            node._Associations?.Konteringar?.some(
              kont => kont._Associations?.Konteringskod?.KodBenamning != null
            ) ?? false,
          standardVerNr: node.StandardVernr || node.VER_NR.toString(),
          underlag: getUnderlag(node),
          referenser: getReferenser(data.ekanalysverifikationreferenser),
          innehallerAttestMeddelanden:
            (getLeverantorHandelse(node?._Associations?.Handelser ?? null)
              ?._Associations?.Levfaktura?._Associations?.Attestmeddelande_All
              ?.length ?? 0) > 0,
          messages: getLeverantorHandelse(
            node?._Associations?.Handelser ?? null
          )?._Associations?.Levfaktura?._Associations?.Attestmeddelande_All,
          sumDebet: node.Summa_Debet,
          sumKredit: node.Summa_Kredit,
        };
        return props.children({
          verifikation,
          error: undefined,
          loading: false,
        });
      }}
    </Query>
  );
};

export default VerifikationQuery;
